import { useAppSelector } from '@/common/reducers/hooks/store.hook';
import { useTranslation } from 'react-i18next';
import { AudioPreview, PdfPreview } from '@common/components/attachment-preview/attachment-preview';
import { Box, Typography, CircularProgress } from '@mui/material';
import { useAssetUrl } from '@common/services/asset-url-resolver';
import { makeSelectMessageDraftById } from '@/components/team-inbox/redux/TeamInboxSelectors';
import { UserMessage } from '@bt-common/schemas/bt/user-message';
import { useState } from 'react';
import MediaPopup from './media-popup';

// Constant reserved height for image/video thumbnails. Every state — URL
// resolving, image bytes loading, loaded, error — occupies exactly this
// height so the bubble never changes size as media settles. That layout
// stability is what keeps the message list pinned to the bottom without the
// jump the user reported (WhatsApp Web reserves media space the same way).
const MEDIA_BOX_HEIGHT = 200;

// Image preview component with asset resolver
function ImagePreview({ url, isClickable, onClick }: { url: string; isClickable?: boolean; onClick?: () => void }) {
    const { t } = useTranslation();
    const { resolvedUrl, isLoading, error } = useAssetUrl(url);

    if (isLoading) {
        return (
            <Box
                sx={{
                    mt: 1,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: `${MEDIA_BOX_HEIGHT}px`,
                    borderRadius: '6px',
                    backgroundColor: 'rgba(0,0,0,0.05)'
                }}
            >
                <CircularProgress size={24} />
            </Box>
        );
    }

    if (error || !resolvedUrl) {
        return (
            <Box
                sx={{
                    mt: 1,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: `${MEDIA_BOX_HEIGHT}px`,
                    borderRadius: '6px',
                    backgroundColor: 'rgba(0,0,0,0.05)',
                    color: 'text.secondary'
                }}
            >
                <Typography variant="caption">{t('scheduledMessage.media.failedToLoadImage')}</Typography>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                mt: 1,
                position: 'relative',
                borderRadius: '6px',
                overflow: 'hidden',
                width: '100%',
                height: `${MEDIA_BOX_HEIGHT}px`,
                backgroundColor: 'rgba(0,0,0,0.05)',
                cursor: isClickable ? 'pointer' : 'default',
            }}
            onClick={isClickable ? onClick : undefined}
        >
            <img
                src={resolvedUrl}
                alt="Attachment"
                style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block'
                }}
            />
        </Box>
    );
}

// Video preview component with asset resolver
function VideoPreview({ url, isClickable, onClick, isGif }: { url: string; isClickable?: boolean; onClick?: () => void; isGif?: boolean }) {
    const { t } = useTranslation();
    const { resolvedUrl, isLoading, error } = useAssetUrl(url);

    if (isLoading) {
        return (
            <Box
                sx={{
                    mt: 1,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: `${MEDIA_BOX_HEIGHT}px`,
                    borderRadius: '6px',
                    backgroundColor: 'rgba(0,0,0,0.05)'
                }}
            >
                <CircularProgress size={24} />
            </Box>
        );
    }

    if (error || !resolvedUrl) {
        return (
            <Box
                sx={{
                    mt: 1,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: `${MEDIA_BOX_HEIGHT}px`,
                    borderRadius: '6px',
                    backgroundColor: 'rgba(0,0,0,0.05)',
                    color: 'text.secondary'
                }}
            >
                <Typography variant="caption">{t('scheduledMessage.media.failedToLoadVideo')}</Typography>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                mt: 1,
                position: 'relative',
                borderRadius: '6px',
                overflow: 'hidden',
                width: '100%',
                height: `${MEDIA_BOX_HEIGHT}px`,
                cursor: isClickable ? 'pointer' : 'default',
            }}
            onClick={isClickable ? onClick : undefined}
        >
            {/* GIF (video/mp4 flagged isGif): autoplay, loop, muted, no
                controls and no play-button overlay — matches WhatsApp, where
                GIFs animate in place the moment they load. */}
            {isGif && (
                <video
                    autoPlay
                    loop
                    muted
                    playsInline
                    preload="auto"
                    src={resolvedUrl}
                    style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        display: 'block'
                    }}
                />
            )}

            {!isGif && isClickable && (
                <Box
                    sx={{
                        position: 'relative',
                        width: '100%',
                        height: '100%',
                        backgroundColor: '#000',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                >
                    <video
                        preload="metadata"
                        src={resolvedUrl}
                        style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            display: 'block'
                        }}
                    />
                    <Box
                        sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: '60px',
                            height: '60px',
                            backgroundColor: 'rgba(0,0,0,0.7)',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                    >
                        <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="white"
                        >
                            <path d="M8 5v14l11-7z" />
                        </svg>
                    </Box>
                </Box>
            )}

            {!isGif && !isClickable && (
                <video
                    controls
                    preload="metadata"
                    src={resolvedUrl}
                    style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        display: 'block'
                    }}
                />
            )}
        </Box>
    );
}

// PDF preview component with asset resolver
function PdfPreviewWithResolver({ url, name, isClickable, onClick }: { url: string; name?: string; isClickable?: boolean; onClick?: () => void }) {
    const { t } = useTranslation();
    const { resolvedUrl, isLoading, error } = useAssetUrl(url);

    if (isLoading) {
        return (
            <Box
                sx={{
                    mt: 1,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: `${MEDIA_BOX_HEIGHT}px`,
                    borderRadius: '6px',
                    backgroundColor: 'rgba(0,0,0,0.05)'
                }}
            >
                <CircularProgress size={24} />
            </Box>
        );
    }

    if (error || !resolvedUrl) {
        return (
            <Box
                sx={{
                    mt: 1,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: `${MEDIA_BOX_HEIGHT}px`,
                    borderRadius: '6px',
                    backgroundColor: 'rgba(0,0,0,0.05)',
                    color: 'text.secondary'
                }}
            >
                <Typography variant="caption">{t('scheduledMessage.media.failedToLoadPdf')}</Typography>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                mt: 1,
                width: '100%',
                position: 'relative',
            }}
        >
            {isClickable && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        zIndex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: isClickable ? 'pointer' : 'default',
                    }}
                    onClick={isClickable ? onClick : undefined}
                >
                    <Box
                        sx={{
                            width: '60px',
                            height: '60px',
                            backgroundColor: 'rgba(0,0,0,0.7)',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                    >
                        <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="white"
                        >
                            <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
                        </svg>
                    </Box>
                </Box>
            )}

            <PdfPreview url={resolvedUrl} maxHeight="200px" />

            <Typography
                sx={{
                    mt: 0.5,
                    fontSize: '0.875rem',
                    color: 'var(--color-primary)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                }}
            >
                {name}
            </Typography>
        </Box>
    );
}

// Audio preview component with asset resolver
function AudioPreviewWithResolver({ url, name }: { url: string; name?: string }) {
    const { t } = useTranslation();
    const { resolvedUrl, isLoading, error } = useAssetUrl(url);

    if (isLoading) {
        return (
            <Box sx={{ mt: 1, width: '100%' }}>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        padding: 2,
                        backgroundColor: 'rgba(0,0,0,0.05)',
                        borderRadius: '6px',
                    }}
                >
                    <CircularProgress size={24} />
                </Box>
            </Box>
        );
    }

    if (error || !resolvedUrl) {
        return (
            <Box sx={{ mt: 1, width: '100%' }}>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        padding: 2,
                        backgroundColor: 'rgba(0,0,0,0.05)',
                        borderRadius: '6px',
                        color: 'text.secondary'
                    }}
                >
                    <Typography variant="caption">{t('scheduledMessage.media.failedToLoadAudio')}</Typography>
                </Box>
            </Box>
        );
    }

    return (
        <Box sx={{ mt: 1, width: '100%' }}>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    padding: 2,
                    backgroundColor: 'rgba(0,0,0,0.05)',
                    borderRadius: '6px',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                        sx={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '50%',
                            backgroundColor: 'var(--color-primary)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0
                        }}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="white">
                            <path d="M14,3.23V5.29C16.89,6.15 19,8.83 19,12C19,15.17 16.89,17.85 14,18.71V20.77C18,19.86 21,16.28 21,12C21,7.72 18,4.14 14,3.23M16.5,12C16.5,10.23 15.5,8.71 14,7.97V16C15.5,15.29 16.5,13.76 16.5,12M3,9V15H7L12,20V4L7,9H3Z" />
                        </svg>
                    </Box>
                    {name && (
                        <Typography
                            variant="body2"
                            sx={{
                                flex: 1,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                color: 'var(--color-text)'
                            }}
                        >
                            {name}
                        </Typography>
                    )}
                </Box>
                <audio
                    controls
                    preload="metadata"
                    style={{
                        width: '100%',
                        height: '32px',
                    }}
                    src={resolvedUrl}
                >
                    Your browser does not support the audio element.
                </audio>
            </Box>
        </Box>
    );
}

type AddScheduledMessageAttachmentPreviewProps = {
    messageId?: string
    messageOverride?: Partial<UserMessage>
    isMediaClickable?: boolean
}

export default function AddScheduledMessageAttachmentPreview({ messageId, messageOverride, isMediaClickable = false }: AddScheduledMessageAttachmentPreviewProps) {

    // Use messageDraft for real-time preview updates (avoids race conditions with wrapper)
    const reduxDraft = useAppSelector(state => makeSelectMessageDraftById(messageId ?? null)(state));
    const messageDraft = messageOverride ?? reduxDraft;

    const [popupOpen, setPopupOpen] = useState(false);

    // Early return if no messageDraft
    if (!messageDraft) {
        return null;
    }

    const assetType = messageDraft.asset?.type;
    const isImage = assetType?.startsWith('image/');
    const isVideo = assetType?.startsWith('video/');
    const isAudio = assetType?.startsWith('audio/');
    const isPdf = assetType?.startsWith('application/pdf');
    // WhatsApp GIFs arrive as video/mp4 flagged isGif; render them animating.
    const isGif = isVideo && messageDraft.asset?.isGif === true;

    const handleMediaClick = (event?: React.MouseEvent) => {
        if (isMediaClickable && (isImage || isVideo || isPdf)) {
            event?.preventDefault();
            event?.stopPropagation();
            setPopupOpen(true);
        }
    };

    const handleClosePopup = () => {
        setPopupOpen(false);
    };


    return (
        <>
            {messageDraft.asset?.url && (
                <>
                    {isImage &&
                        <ImagePreview
                            url={messageDraft.asset.url}
                            isClickable={isMediaClickable}
                            onClick={handleMediaClick}
                        />
                    }
                    {isVideo &&
                        <VideoPreview url={messageDraft.asset.url}
                            isClickable={isMediaClickable}
                            onClick={handleMediaClick}
                            isGif={isGif}
                        />
                    }
                    {isAudio &&
                        <AudioPreviewWithResolver url={messageDraft.asset.url}
                            name={messageDraft.asset.name ?? undefined}
                        />
                    }
                    {isPdf &&
                        <PdfPreviewWithResolver
                            url={messageDraft.asset.url}
                            name={messageDraft.asset.name ?? undefined}
                            isClickable={isMediaClickable}
                            onClick={handleMediaClick}
                        />
                    }
                    {/* Media Popup - For images, videos, and PDFs */}
                    {messageDraft.asset?.url && (isImage || isVideo || isPdf) && (
                        <MediaPopup
                            open={popupOpen}
                            onClose={handleClosePopup}
                            mediaUrl={messageDraft.asset.url}
                            mediaType={messageDraft.asset.type ?? ""}
                            mediaName={messageDraft.asset.name ?? undefined}
                            isGif={isGif}
                        />
                    )}
                </>
            )}


        </>
    )
}